/* eslint-disable @typescript-eslint/ban-types */
import { User } from '@pjn/core/model/app/User'
import { Letrado } from '@pjn/core/model/app/Letrado'
import {
  default as RealKeycloak,
  KeycloakConstructorParams,
  KeycloakPromise,
  KeycloakRedirectUri
} from 'keycloak-js'
import { isIE } from '@pjn/utils/browser'
import { SecurityProvider } from '@pjn/core/security'
import { extractUserPropsFromToken } from './KeycloakTokenMapper'

export { default as RealKeycloak } from 'keycloak-js'

/**
 * Adaptador con el IdentityProvider (ej: Keycloak) que permite hacer login, logout, obtener
 * datos del usuario y obtener el token para los request.
 *
 * @export
 * @class Keycloak
 * @implements {SecurityProvider}
 */
export default class Keycloak implements SecurityProvider {
  keycloak: RealKeycloak
  clientId: string
  onTokenExpired?: Function
  /*public onAuthSuccess:Function;
  public onAuthError:Function;*/
  onAuthLogout?: Function
  // loginRequired: boolean = false;
  initialized = false

  constructor(param: KeycloakConstructorParams) {
    this.clientId = param.clientId
    this.keycloak = new RealKeycloak(param)
    // this.loginRequired = !!param.loginRequired;
    this.addHandlers()
  }

  private handleLogout() {
    if (this.onAuthLogout) this.onAuthLogout()
  }

  private handleTokenExpired() {
    this.updateToken()
    if (this.onTokenExpired) this.onTokenExpired()
  }

  /**
   * Actualiza el token si expira en menos de {minValidity} segundos.
   *
   * @param {number} [minValidity] Opcional. Si no se especifica se usa 0.
   * @returns
   * @memberof Keycloak
   */
  updateToken(minValidity?: number) {
    return translatePromise(this.keycloak.updateToken(minValidity)).catch(
      () => {
        this.keycloak.clearToken()
        throw new Error('Error updating token')
      }
    )
  }

  get authenticated() {
    return this.keycloak.authenticated
  }

  getUser(): User | undefined {
    if (!this.authenticated) return undefined

    const props = extractUserPropsFromToken(this.tokenInfo)

    if (props.matricula) {
      // data.matricula = ti.matricula;
      // data.cuil = ti.cuil;
      return new Letrado(props)
    } else {
      return new User(props)
    }
  }

  get token() {
    return this.keycloak.token
  }
  get idToken() {
    return this.keycloak.idToken
  }

  /**
   * Informacion parseada del token
   *
   * @readonly
   * @type {*}
   */
  get tokenInfo(): any {
    return Object.assign(
      {},
      this.keycloak.tokenParsed,
      this.keycloak.idTokenParsed
    )
  }

  hasResourceRole(role: string, resource?: string) {
    return this.keycloak.hasResourceRole(role, resource)
  }

  hasRealmRole(role: string) {
    return this.keycloak.hasRealmRole(role)
  }

  getGlobalRoles(): string[] {
    return this.keycloak.realmAccess ? this.keycloak.realmAccess.roles : []
  }

  getApplicationRoles(resource: string = this.clientId) {
    if (!this.keycloak.resourceAccess) return []
    return this.keycloak.resourceAccess[resource].roles
  }

  /**
   * Inicializa el provider, requiriendo autenticacion, por lo que se muestra la pantalla de login si el usuario
   * aun no se autentico.
   *
   * @returns una promise que se resuelve cuando se termino el login o falla si no se puede autenticar
   * @memberof Keycloak
   */
  start(loginRequired: boolean) {
    const checkLoginIframe = !isIE()
    if (!checkLoginIframe)
      console.log(
        'El navegador es Internet Explorer, se deshabilita el IFrame de Login'
      )

    return translatePromise(
      this.keycloak.init({
        onLoad: loginRequired ? 'login-required' : 'check-sso',
        checkLoginIframe
      })
    ).then(() => {
      this.initialized = true
    })
    // .then( () => {
    //   setTimeout(() => removeParamsFromUri(["code", "state"]), 100);
    //   return true;
    // });
  }

  logout(options?: KeycloakRedirectUri): Promise<void> {
    return translatePromise(this.keycloak.logout(options))
  }

  login(): Promise<void> {
    if (!this.initialized) return this.start(true)
    if (!this.authenticated) return translatePromise(this.keycloak.login())
    return Promise.resolve()
  }

  /**
   * Obtiene el token de acceso, actualizandolo primero en caso de ser necesario.
   *
   * @returns {Promise<string>} una promise que se resuelve con el token o falla.
   * @memberof Keycloak
   */
  getToken(): Promise<string> {
    return this.updateToken(5).then(() => this.token!)
  }

  isAllowed(permission: string, checkGlobal = false): boolean {
    // Si tiene el permiso a nivel aplicacion, se devuelve true
    // En caso contrario, si se permite chequear global, se chequea en el realm.
    return (
      this.hasResourceRole(permission) ||
      (checkGlobal && this.hasRealmRole(permission))
    )
  }

  private addHandlers() {
    this.keycloak.onTokenExpired = this.handleTokenExpired.bind(this)
    this.keycloak.onAuthLogout = this.handleLogout.bind(this)
  }
}

/**
 * Traduce las promises custom de Keycloak a promises estandar para poder usarlas con async/await
 *
 * @template T
 * @param {KeycloakPromise<T>} p
 * @returns {Promise<T>}
 */
function translatePromise<T>(p: KeycloakPromise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    p.success(resolve).error(reject)
  })
}
